import { useMemo } from 'react';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import { PortalDsSectionHeader } from 'src/components/portal-design-system';
import type { ReportingApiClientRow } from '../types/reporting-api-usage.types';
import {
  formatCount,
  formatDateTime,
  formatQuotaConsumption,
  formatRatePercent,
} from '../utils/reporting-api-usage.utils';

interface ReportingApiClientsTableProps {
  rows: ReportingApiClientRow[];
  onSelectClient: (clientId: string) => void;
}

export function ReportingApiClientsTable({
  rows,
  onSelectClient,
}: ReportingApiClientsTableProps) {
  const columns = useMemo<DataTableColumn<ReportingApiClientRow>[]>(() => [
    {
      id: 'client',
      header: 'Client',
      accessor: (row) => row.client_title,
      render: (row) => (
        <button
          type="button"
          className="font-medium text-[#2563EB] hover:underline"
          onClick={() => onSelectClient(row.client_id)}
        >
          {row.client_title}
        </button>
      ),
    },
    {
      id: 'active_keys',
      header: 'Active keys',
      accessor: (row) => row.active_key_count,
      align: 'right',
      render: (row) => formatCount(row.active_key_count),
    },
    {
      id: 'requests',
      header: 'Requests',
      accessor: (row) => row.request_count,
      align: 'right',
      render: (row) => formatCount(row.request_count),
    },
    {
      id: 'cache_hit_rate',
      header: 'Cache hit rate',
      accessor: (row) => row.cache_hit_rate,
      align: 'right',
      render: (row) => formatRatePercent(row.cache_hit_rate),
    },
    {
      id: 'errors',
      header: 'Errors',
      accessor: (row) => row.error_count,
      align: 'right',
      render: (row) => formatCount(row.error_count),
    },
    {
      id: 'last_activity',
      header: 'Last activity',
      accessor: (row) => row.last_activity ?? '',
      render: (row) => formatDateTime(row.last_activity),
    },
    {
      id: 'daily_requests',
      header: 'Daily requests',
      accessor: (row) => row.quotas.find((item) => item.name === 'daily_requests')?.current ?? 0,
      align: 'right',
      render: (row) => {
        const quota = row.quotas.find((item) => item.name === 'daily_requests');
        return quota ? formatQuotaConsumption(quota) : '—';
      },
    },
  ], [onSelectClient]);

  return (
    <section className="space-y-3">
      <PortalDsSectionHeader title="Clients with API keys" />
      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.client_id}
        onRowClick={(row) => onSelectClient(row.client_id)}
        emptyMessage="No clients have API keys assigned."
      />
    </section>
  );
}
